/**
 * Release store — tenant branches and change requests (release service).
 *
 * A branch is a forked tenant that tracks `main` (the tenant that operates);
 * a change request is the reviewed path back into main. Failed reads clear
 * stale data and expose an inline error. Writes preserve structured API errors.
 */
import { create } from 'zustand';
import { getTenantId, getAccessToken } from './authStore';

const RELEASE_API = import.meta.env.VITE_RELEASE_SERVICE_URL || 'http://localhost:8036';

function headers(json = true): Record<string, string> {
  const h: Record<string, string> = { 'x-tenant-id': getTenantId() };
  const t = getAccessToken();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

// ── Contract types ───────────────────────────────────────────────────────────

export type BranchStatus = 'creating' | 'active' | 'in_review' | 'merged' | 'archived' | 'failed';
export type ChangeRequestStatus =
  | 'in_review' | 'changes_requested' | 'approved' | 'merged'
  | 'partial' | 'failed' | 'rejected' | 'withdrawn' | 'merging' | 'interrupted';
export type ReviewDecision = 'approve' | 'request_changes' | 'reject';
export type CheckStatus = 'ok' | 'warn' | 'fail' | 'skip';
export type ForkMode = 'selected' | 'all' | 'none';

export interface ReleasePolicy {
  tenant_id: string;
  protected: boolean;
  min_approvals: number;
  author_not_approver: boolean;
  max_active_branches: number;
  max_fork_rows: number;
  ttl_days: number;
}

export interface ForkData {
  mode: ForkMode;
  object_types: string[];
  events_days: number;
}

export interface ForkReport {
  config?: number;
  records?: number;
  events?: number;
  took_ms?: number;
  error?: string;
}

export interface VsMainRow {
  kind: string;
  key: string;
  name: string;
  op: string;
  status: string;
  /** What differs, e.g. "description: «» → «x»; properties: +canal". Null for a creation or a removal. */
  changes?: string | null;
}

export interface VsMainSummary {
  added: number;
  modified: number;
  deleted: number;
  main_moved: number;
  conflicts: number;
  /** One row per resource that differs; only the single-branch status carries it. */
  rows?: VsMainRow[];
}

export interface MainBranch {
  tenant_id: string;
  protected: boolean;
  name: 'main';
}

export interface Branch {
  name: string;
  tenant_id: string;
  status: BranchStatus;
  owner_kind: string;
  owner_ref: string;
  owner_label: string;
  fork_data: ForkData;
  fork_report: ForkReport;
  change_request_id: string | null;
  created_at: string;
  expires_at: string | null;
  last_activity_at: string | null;
  vs_main?: VsMainSummary;
}

export interface BranchEvidence {
  pipeline_runs: number;
  logic_runs: number;
  last_run_ok: boolean | null;
}

export interface BranchDetail {
  branch: Branch;
  vs_main: VsMainSummary & { rows: VsMainRow[] };
  evidence: BranchEvidence;
}

export interface ChangeRequestSummary {
  id: string;
  number: number;
  title: string;
  status: ChangeRequestStatus;
  kind: string;
  branch_name: string;
  source_tenant_id: string;
  target_tenant_id: string;
  author_kind: string;
  author_ref: string;
  author_label: string;
  item_count: number;
  checks_summary: { ok: number; warn: number; fail: number };
  reviews_summary: { required: number; approved: number };
  created_at: string;
  submitted_at: string | null;
  merged_at: string | null;
}

export interface ItemDiff {
  op: string;
  summary: string;
  fields: Record<string, { before: unknown; after: unknown }>;
  collections: Record<string, { added: string[]; removed: string[]; changed: string[] }>;
}

export interface ChangeRequestItem {
  id: string;
  kind: string;
  key: string;
  name: string;
  op: string;
  destructive: string[];
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  apply_result?: { state?: string; ok?: boolean; error?: string };
  diff: ItemDiff;
}

export interface ChangeRequestCheck {
  id: string;
  check_name: string;
  item_id: string | null;
  status: CheckStatus;
  detail: { message?: string; [k: string]: unknown };
  run_at: string;
}

export interface ChangeRequestReview {
  id: string;
  reviewer_user_id: string;
  reviewer_email: string;
  required: boolean;
  decision: ReviewDecision | null;
  note: string | null;
  decided_at: string | null;
}

export interface ChangeRequestComment {
  id: string;
  author_label: string;
  author_kind: string;
  body: string;
  created_at: string;
}

export interface ChangeRequestEvent {
  id: string;
  at: string;
  actor: string;
  event: string;
  payload: Record<string, unknown> | null;
}

export interface EvidenceRun {
  kind: string;
  name: string;
  at: string;
  status: string;
  summary: string;
}

export interface ChangeRequestDetail {
  id: string;
  number: number;
  title: string;
  rationale: string;
  status: ChangeRequestStatus;
  kind: string;
  branch_name: string;
  author_label: string;
  author_kind: string;
  min_approvals: number;
  evidence: { pipeline_runs: number; logic_runs: number; runs: EvidenceRun[] };
  main_moved: { kind: string; key: string; name: string }[];
  items: ChangeRequestItem[];
  checks: ChangeRequestCheck[];
  reviews: ChangeRequestReview[];
  comments: ChangeRequestComment[];
  events: ChangeRequestEvent[];
  can_approve: boolean;
  approval_block_reason?: string;
  can_merge: boolean;
  can_recover?: boolean;
  merge_report?: unknown;
  created_at?: string;
  submitted_at?: string | null;
  merged_at?: string | null;
}

export interface MergeResult {
  status: 'merged' | 'partial' | 'failed' | 'interrupted';
  report: unknown[];
}

/** Error thrown by writes: `detail` keeps the structured body (e.g. the merge gate's checks). */
export class ReleaseApiError extends Error {
  status: number;
  detail: unknown;
  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = 'ReleaseApiError';
    this.status = status;
    this.detail = detail;
  }
}

function detailMessage(detail: unknown, status: number): string {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const d = detail as { error?: unknown; message?: unknown };
    if (typeof d.error === 'string') return d.error;
    if (typeof d.message === 'string') return d.message;
  }
  return `Error ${status}`;
}

async function readError(res: Response): Promise<never> {
  const body = await res.json().catch(() => ({}));
  const detail = (body as { detail?: unknown }).detail;
  throw new ReleaseApiError(detailMessage(detail, res.status), res.status, detail);
}

// ── Store ────────────────────────────────────────────────────────────────────

interface ReleaseState {
  policy: ReleasePolicy | null;
  main: MainBranch | null;
  branches: Branch[];
  changeRequests: ChangeRequestSummary[];
  current: ChangeRequestDetail | null;
  /** Count of change requests in `in_review` — the nav badge. */
  pendingApprovals: number;
  loading: boolean;
  error: string | null;
  /** Change request the Changes page should open on mount (set by Branches after requesting approval). */
  pendingOpenId: string | null;

  fetchPolicy: () => Promise<void>;
  updatePolicy: (patch: Partial<Omit<ReleasePolicy, 'tenant_id'>>) => Promise<ReleasePolicy>;
  /** `keepComparison` carries each branch's last `vs_main` over (polling while a fork runs must not redo every comparison). */
  fetchBranches: (opts?: { keepComparison?: boolean }) => Promise<void>;
  fetchBranch: (name: string) => Promise<void>;
  createBranch: (name: string, fork_data: ForkData) => Promise<{ branch: Branch; fork_report: ForkReport }>;
  deleteBranch: (name: string) => Promise<void>;
  rebaseBranch: (name: string) => Promise<{ branch: Branch; conflicts: unknown[]; applied: number }>;
  /** Copy main's current data into the branch again (POST /branches/{name}/refresh-data). */
  refreshBranchData: (name: string, opts?: { objectTypes?: string[]; eventsDays?: number }) => Promise<Record<string, unknown>>;
  requestApproval: (name: string, title: string, rationale: string) => Promise<ChangeRequestSummary>;
  fetchChangeRequests: (status?: string) => Promise<void>;
  fetchChangeRequest: (id: string) => Promise<ChangeRequestDetail | null>;
  decide: (id: string, decision: ReviewDecision, note?: string, ack?: boolean) => Promise<ChangeRequestDetail>;
  recover: (id: string, note: string) => Promise<void>;
  merge: (id: string) => Promise<MergeResult>;
  rerunChecks: (id: string) => Promise<ChangeRequestCheck[]>;
  comment: (id: string, body: string) => Promise<ChangeRequestComment>;
  withdraw: (id: string) => Promise<void>;
  setPendingOpenId: (id: string | null) => void;
  /** Drop all tenant-scoped state on sign-out / user switch (see store/sessionReset). */
  reset: () => void;
}

const INITIAL = {
  policy: null,
  main: null,
  branches: [],
  changeRequests: [],
  current: null,
  pendingApprovals: 0,
  loading: false,
  error: null,
  pendingOpenId: null,
};

export const useReleaseStore = create<ReleaseState>((set, get) => ({
  ...INITIAL,

  fetchPolicy: async () => {
    try {
      const res = await fetch(`${RELEASE_API}/policy`, { headers: headers(false) });
      if (!res.ok) await readError(res);
      set({ policy: await res.json(), error: null });
    } catch (error) {
      set({ policy: null, error: error instanceof Error ? error.message : "No se pudo cargar la política" });
    }
  },

  updatePolicy: async (patch) => {
    const res = await fetch(`${RELEASE_API}/policy`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify(patch),
    });
    if (!res.ok) await readError(res);
    const policy: ReleasePolicy = await res.json();
    set({ policy });
    return policy;
  },

  fetchBranches: async (opts) => {
    set({ loading: true });
    try {
      const res = await fetch(`${RELEASE_API}/branches`, { headers: headers(false) });
      if (!res.ok) await readError(res);
      const data = await res.json();
      const previous = new Map(get().branches.map((b) => [b.name, b.vs_main]));
      const fresh: Branch[] = Array.isArray(data.branches) ? data.branches : [];
      const branches = opts?.keepComparison
        ? fresh.map((b) => (previous.get(b.name) && !b.vs_main ? { ...b, vs_main: previous.get(b.name) } : b))
        : fresh;
      set({ main: data.main ?? null, branches, error: null });
    } catch (error) {
      set({ branches: [], main: null, error: error instanceof Error ? error.message : "No se pudieron cargar las ramas" });
    } finally {
      set({ loading: false });
    }
  },

  fetchBranch: async (name) => {
    const res = await fetch(`${RELEASE_API}/branches/${encodeURIComponent(name)}`, { headers: headers(false) });
    if (!res.ok) await readError(res);
    const detail = await res.json();
    if (detail.vs_main?.error) throw new Error(detail.vs_main.error);
    set((s) => ({ branches: s.branches.map((b) => b.name === name ? { ...detail.branch, vs_main: detail.vs_main } : b) }));
  },

  createBranch: async (name, fork_data) => {
    const res = await fetch(`${RELEASE_API}/branches`, {
      method: 'POST', headers: headers(), body: JSON.stringify({ name, fork_data }),
    });
    if (!res.ok) await readError(res);
    const data = await res.json();
    await get().fetchBranches();
    return data;
  },

  deleteBranch: async (name) => {
    const res = await fetch(`${RELEASE_API}/branches/${encodeURIComponent(name)}`, {
      method: 'DELETE', headers: headers(false),
    });
    if (!res.ok) await readError(res);
    set((s) => ({ branches: s.branches.filter((b) => b.name !== name) }));
  },

  rebaseBranch: async (name) => {
    const res = await fetch(`${RELEASE_API}/branches/${encodeURIComponent(name)}/rebase`, {
      method: 'POST', headers: headers(),
    });
    if (!res.ok) await readError(res);
    const data = await res.json();
    await get().fetchBranches();
    return data;
  },

  refreshBranchData: async (name, opts) => {
    const res = await fetch(`${RELEASE_API}/branches/${encodeURIComponent(name)}/refresh-data`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ object_types: opts?.objectTypes ?? null, events_days: opts?.eventsDays ?? null }),
    });
    if (!res.ok) await readError(res);
    const data = await res.json();
    await get().fetchBranch(name).catch(() => undefined);
    return (data && typeof data === 'object' && 'report' in data ? (data as { report: Record<string, unknown> }).report : data) as Record<string, unknown>;
  },

  requestApproval: async (name, title, rationale) => {
    const res = await fetch(`${RELEASE_API}/branches/${encodeURIComponent(name)}/request-approval`, {
      method: 'POST', headers: headers(), body: JSON.stringify({ title, rationale }),
    });
    if (!res.ok) await readError(res);
    const data = await res.json();
    await get().fetchBranches();
    return data.change_request;
  },

  fetchChangeRequests: async (status?) => {
    try {
      const params = status ? `?status=${encodeURIComponent(status)}` : '';
      const res = await fetch(`${RELEASE_API}/change-requests${params}`, { headers: headers(false) });
      if (!res.ok) await readError(res);
      const data = await res.json();
      set({ error: null });
      const list: ChangeRequestSummary[] = Array.isArray(data.change_requests) ? data.change_requests : [];
      const pendingApprovals = list.filter((c) => c.status === 'in_review').length;
      // A filtered fetch only refreshes the badge when it was the in_review
      // slice; the unfiltered list always is the source of truth for both.
      if (!status) set({ changeRequests: list, pendingApprovals });
      else if (status === 'in_review') set({ changeRequests: list, pendingApprovals });
      else set({ changeRequests: list });
    } catch (error) {
      set({ changeRequests: [], error: error instanceof Error ? error.message : "No se pudieron cargar las solicitudes" });
    }
  },

  fetchChangeRequest: async (id) => {
    try {
      const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}`, { headers: headers(false) });
      if (!res.ok) await readError(res);
      const detail: ChangeRequestDetail = await res.json();
      set({ current: detail, error: null });
      return detail;
    } catch (error) {
      set({ current: null, error: error instanceof Error ? error.message : "No se pudo cargar la solicitud" });
      return null;
    }
  },

  decide: async (id, decision, note, ack) => {
    const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}/reviews/decide`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ decision, note: note || undefined, ack_destructive: ack || undefined }),
    });
    if (!res.ok) await readError(res);
    const detail: ChangeRequestDetail = await res.json();
    set({ current: detail });
    return detail;
  },

  merge: async (id) => {
    const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}/merge`, {
      method: 'POST', headers: headers(),
    });
    if (!res.ok) await readError(res);
    const result: MergeResult = await res.json();
    await get().fetchChangeRequest(id);
    return result;
  },

  recover: async (id, note) => {
    const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}/recover`, {
      method: 'POST', headers: headers(), body: JSON.stringify({ note, confirmed: true }),
    });
    if (!res.ok) await readError(res);
    set({ current: await res.json() });
    await get().fetchChangeRequests();
  },

  rerunChecks: async (id) => {
    const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}/checks`, {
      method: 'POST', headers: headers(),
    });
    if (!res.ok) await readError(res);
    const data = await res.json();
    const checks: ChangeRequestCheck[] = Array.isArray(data.checks) ? data.checks : [];
    set((s) => (s.current && s.current.id === id ? { current: { ...s.current, checks } } : {}));
    return checks;
  },

  comment: async (id, body) => {
    const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}/comments`, {
      method: 'POST', headers: headers(), body: JSON.stringify({ body }),
    });
    if (!res.ok) await readError(res);
    const c: ChangeRequestComment = await res.json();
    set((s) => (s.current && s.current.id === id
      ? { current: { ...s.current, comments: [...s.current.comments, c] } }
      : {}));
    return c;
  },

  withdraw: async (id) => {
    const res = await fetch(`${RELEASE_API}/change-requests/${encodeURIComponent(id)}`, {
      method: 'DELETE', headers: headers(false),
    });
    if (!res.ok) await readError(res);
    set((s) => ({
      current: s.current && s.current.id === id ? null : s.current,
      changeRequests: s.changeRequests.map((c) => (c.id === id ? { ...c, status: 'withdrawn' as const } : c)),
    }));
  },

  setPendingOpenId: (id) => set({ pendingOpenId: id }),

  reset: () => set({ ...INITIAL }),
}));
